import { IntlShape } from 'react-intl';

/**
 * Utility functions for translating unit type names
 * Handles mapping common unit types to localized strings with fallback to backend values
 */

interface UnitTypeTranslation {
  singular: string;
  plural: string;
}

type UnitTypeMap = Record<string, UnitTypeTranslation>;

interface UnitTypeLabelOptions {
  lowercase?: boolean;
}

/**
 * Get the translated label for a unit type
 * @param intl - react-intl intl object
 * @param singular - singular form from backend
 * @param plural - plural form from backend
 * @param isPlural - whether to return plural form
 * @param options - optional settings (e.g. lowercase for mid-sentence use)
 * @returns translated label or backend value as fallback
 */
export const getUnitTypeLabel = (
  intl: IntlShape,
  singular: string,
  plural: string,
  isPlural: boolean,
  options?: UnitTypeLabelOptions
): string => {
  const backendValue = isPlural ? plural : singular;

  // Normalize to lowercase for comparison
  const normalizedValue = backendValue?.toLowerCase() || '';

  // Map common unit types to translated strings
  const unitTypeMap: UnitTypeMap = {
    adult: {
      singular: intl.formatMessage({
        defaultMessage: 'Adult',
        description: 'Singular form of adult unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'Adults',
        description: 'Plural form of adult unit type',
      }),
    },
    child: {
      singular: intl.formatMessage({
        defaultMessage: 'Child',
        description: 'Singular form of child unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'Children',
        description: 'Plural form of child unit type',
      }),
    },
    guest: {
      singular: intl.formatMessage({
        defaultMessage: 'Guest',
        description: 'Singular form of guest unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'Guests',
        description: 'Plural form of guest unit type',
      }),
    },
    person: {
      singular: intl.formatMessage({
        defaultMessage: 'Person',
        description: 'Singular form of person unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'People',
        description: 'Plural form of person unit type',
      }),
    },
    ticket: {
      singular: intl.formatMessage({
        defaultMessage: 'Ticket',
        description: 'Singular form of ticket unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'Tickets',
        description: 'Plural form of ticket unit type',
      }),
    },
    senior: {
      singular: intl.formatMessage({
        defaultMessage: 'Senior',
        description: 'Singular form of senior unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'Seniors',
        description: 'Plural form of senior unit type',
      }),
    },
    minor: {
      singular: intl.formatMessage({
        defaultMessage: 'Minor',
        description: 'Singular form of minor unit type',
      }),
      plural: intl.formatMessage({
        defaultMessage: 'Minors',
        description: 'Plural form of minor unit type',
      }),
    },
  };

  // Try to find a matching unit type
  const matchingKey = Object.keys(unitTypeMap).find(
    (key) =>
      key === normalizedValue ||
      unitTypeMap[key].singular.toLowerCase() === normalizedValue ||
      unitTypeMap[key].plural.toLowerCase() === normalizedValue
  );

  const result = matchingKey
    ? isPlural
      ? unitTypeMap[matchingKey].plural
      : unitTypeMap[matchingKey].singular
    : backendValue;

  return options?.lowercase ? result.toLowerCase() : result;
};
